import { useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Button } from "../ui/button";
import { useI18n } from "../../lib/i18n";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { FileDown, ClipboardCopy, Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

export const ExportTools = ({ analysis, canExport, captureId = "analysis-export-target" }) => {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildClipboardText = () => {
    const lines = [
      `CANDLE.AI Analysis — ${analysis.symbol || "—"} ${analysis.timeframe ? `· ${analysis.timeframe}` : ""}`.trim(),
      `Direction: ${analysis.direction} (${analysis.confidence}%)`,
    ];
    if (analysis.entry) lines.push(`Entry: ${analysis.entry}`);
    if (analysis.tp1) lines.push(`TP1: ${analysis.tp1}`);
    if (analysis.tp2) lines.push(`TP2: ${analysis.tp2}`);
    if (analysis.stop_loss) lines.push(`Stop-Loss: ${analysis.stop_loss}`);
    if (analysis.risk_reward) lines.push(`R/R: ${analysis.risk_reward}`);
    if (analysis.invalidation) lines.push(`Invalidation: ${analysis.invalidation}`);
    lines.push("---", "Generated by CANDLE.AI");
    return lines.join("\n");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildClipboardText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const node = document.getElementById(captureId);
      if (!node) throw new Error("Target not found");
      const canvas = await html2canvas(node, {
        backgroundColor: "#050505",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const ratio = canvas.height / canvas.width;
      const imgW = pageWidth - 16;
      const imgH = imgW * ratio;
      let y = 12;
      // Header line
      pdf.setFillColor(5, 5, 5);
      pdf.rect(0, 0, pageWidth, pageHeight, "F");
      pdf.setTextColor(0, 255, 136);
      pdf.setFontSize(10);
      pdf.text("CANDLE.AI", 8, 8);
      pdf.setTextColor(150, 150, 150);
      pdf.text(new Date().toLocaleString(), pageWidth - 8, 8, { align: "right" });
      // Image
      if (imgH > pageHeight - 24) {
        // multi-page
        let position = 12;
        const scaledH = imgH;
        pdf.addImage(imgData, "PNG", 8, position, imgW, scaledH);
        let remaining = scaledH - (pageHeight - 24);
        while (remaining > 0) {
          pdf.addPage();
          position = position - (pageHeight - 24);
          pdf.setFillColor(5, 5, 5);
          pdf.rect(0, 0, pageWidth, pageHeight, "F");
          pdf.addImage(imgData, "PNG", 8, position, imgW, scaledH);
          remaining -= (pageHeight - 24);
        }
      } else {
        pdf.addImage(imgData, "PNG", 8, 12, imgW, imgH);
      }
      // Footer
      pdf.setTextColor(110, 110, 110);
      pdf.setFontSize(7);
      pdf.text(
        "CANDLE.AI is an analytical tool only. Not financial advice.",
        8,
        pageHeight - 6,
      );
      const fname = `CANDLEAI-${analysis.symbol || "chart"}-${analysis.timeframe || "TF"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fname);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="export-tools">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={!canExport || exporting}
                className="rounded-none border-white/15 bg-transparent hover:bg-white/5 h-9 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="export-pdf-btn"
              >
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> :
                  canExport ? <FileDown className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> :
                  <Lock className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />}
                <span className="font-mono text-xs">{t("export_pdf")}</span>
              </Button>
            </span>
          </TooltipTrigger>
          {!canExport && (
            <TooltipContent className="bg-[#0a0a0a] border-white/10 rounded-none font-mono text-xs">
              {t("export_upgrade_tooltip")}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopy}
        className="rounded-none border-white/15 bg-transparent hover:bg-white/5 h-9"
        data-testid="copy-levels-btn"
      >
        {copied ? <><Check className="h-3.5 w-3.5 mr-1.5 text-bull" strokeWidth={2} /><span className="font-mono text-xs text-bull">{t("copy_done")}</span></> :
          <><ClipboardCopy className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /><span className="font-mono text-xs">{t("copy_levels")}</span></>}
      </Button>
    </div>
  );
};
